// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  cvFileHasStored,
  downloadCvFile,
  fmtDate,
  fmtDateTime,
  fmtSize,
  fileIcon,
  getLatestAppForJob,
  transcriptLinesForApplication,
  patchApplicationById,
  transcriptAvailableForJob,
  portalStatusLabel,
  applicationRoleStatus,
  applicationRoleStatusLabel,
  applicationVoiceInterviewCompleted,
  interviewStartSlotStatus,
  resolveApplicationInterviewLanguage,
  SB,
  apiFetchInit,
} from "@/legacy/helpersModule";
import { Badge } from "@/shared/components/ui/Badge";
import { Button, Textarea, useToast } from "@/shared/components/ui";
import { InterviewTranscriptPanel, buildTranscriptSections } from "@/features/hr/components/InterviewTranscriptPanel";
import { InterviewScheduleModal } from "@/features/hr/components/InterviewScheduleModal";
import { hrResetCandidatePassword } from "@/shared/api/candidatesApi";
import { exportTranscriptPdf } from "@/shared/pdf/exportPdf";
import { useEscapeKey } from "@/shared/hooks/useEscapeKey";
export function CandidateDetail({ candidate, jobs, onUpdate, onInterview, onAnalysis, onReschedule, autoOpenReschedule = false, onBack }) {
  const toast = useToast();
  useEscapeKey(onBack);
  const [showCV, setShowCV] = useState(false), [showTranscript, setShowTranscript] = useState(true), [remarks, setRemarks] = useState(candidate.remarks || ""), [flash, setFlash] = useState("");
  const [newPw, setNewPw] = useState(""), [confirmPw, setConfirmPw] = useState(""), [pwErr, setPwErr] = useState(""), [pwBusy, setPwBusy] = useState(false);
  const [detailTab, setDetailTab] = useState("timeline");
  const [interviewAns, setInterviewAns] = useState(null);
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);
  useEffect(() => {
    if (autoOpenReschedule) setShowReschedule(true);
  }, [autoOpenReschedule]);
  const appliedList = (candidate.applicationHistory || []).map(a => ({ ...a, job: jobs.find(j => j.id === a.jobId) })).filter(a => a.job);
  useEffect(() => {
    const valid = new Set((candidate.applicationHistory || []).map((a) => a.applicationId).filter((id) => id != null));
    setSelectedApplicationId((prev) => {
      if (prev != null && valid.has(prev)) return prev;
      return getLatestAppForJob(candidate.applicationHistory, candidate.jobId)?.applicationId ?? [...valid][0] ?? null;
    });
  }, [candidate.id, candidate.jobId, JSON.stringify((candidate.applicationHistory || []).map((a) => [a.applicationId, a.jobId]))]);
  const selectedAppRow = appliedList.find((a) => a.applicationId === selectedApplicationId) || null;
  const transcriptLang = resolveApplicationInterviewLanguage(selectedAppRow, candidate);
  useEffect(() => {
    if (selectedAppRow) {
      const appRm = selectedAppRow.hrRemarks != null ? String(selectedAppRow.hrRemarks) : "";
      setRemarks(appRm);
    } else {
      setRemarks(candidate.remarks || "");
    }
  }, [selectedApplicationId, candidate.id, candidate.remarks, selectedAppRow]);
  const bubbleTranscript = transcriptLinesForApplication(candidate, selectedApplicationId);
  const analysisForSelected = selectedAppRow?.analysis || candidate.analysis;
  // A scheduled slot whose start time + grace window has elapsed with no
  // completed interview is a MISSED slot. Mirrors the backend MISSED derivation
  // (scheduled + grace < now) so the badge here matches the dashboard.
  const appSlotMissed = (a) =>
    Boolean(a?.interviewScheduledAt) &&
    interviewStartSlotStatus(a.interviewScheduledAt, false).tooLate &&
    !applicationVoiceInterviewCompleted(a);
  const rawHeaderStatusLabel =
    selectedAppRow?.hrDecisionStatus === "POTENTIAL" ||
    (!selectedAppRow && candidate.status === "POTENTIAL")
      ? "POTENTIAL"
      : portalStatusLabel(candidate, selectedAppRow?.jobId || candidate.jobId);
  const headerStatusLabel =
    rawHeaderStatusLabel === "SCHEDULED" && appSlotMissed(selectedAppRow)
      ? "MISSED"
      : rawHeaderStatusLabel;
  // For a REATTMPT badge, clarify who the reattempt is waiting on so HR can tell
  // at a glance whether the candidate still needs to submit the request or HR
  // needs to act. Only meaningful while the interview is incomplete_technical.
  const reattemptPendingSide = (a) => {
    if (a?.interviewCompletionStatus !== "incomplete_technical") return null;
    const rs = a?.reattemptRequestStatus || "none";
    if (rs === "pending") return "pending from HR side";
    if (rs === "rejected") return "reattempt declined – candidate to re-apply";
    if (rs === "approved") return null;
    return "pending from candidate side";
  };
  const headerReattemptNote =
    headerStatusLabel === "REATTMPT" ? reattemptPendingSide(selectedAppRow) : null;
  const headerStatusKey =
    headerStatusLabel !== "—" ? headerStatusLabel : candidate.status;
  const hasStoredCv = cvFileHasStored(candidate.cvFile);
  const showCvSection = Boolean(String(candidate.cv || "").trim()) || hasStoredCv;
  const submitHrPasswordReset = async () => {
    setPwErr("");
    if (newPw.length < 6) {
      setPwErr("Password must be at least 6 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setPwErr("Passwords do not match.");
      return;
    }
    if (!window.confirm(`Set a new login password for ${candidate.name}?`)) return;
    setPwBusy(true);
    try {
      await hrResetCandidatePassword(candidate.id, newPw);
      setNewPw("");
      setConfirmPw("");
      setFlash("Password updated — share the new password with the candidate securely.");
      setTimeout(() => setFlash(""), 5000);
    } catch (e) {
      setPwErr(e.message || "Reset failed");
    } finally {
      setPwBusy(false);
    }
  };
  const setStatus = (s) => {
    if (selectedApplicationId != null) {
      onUpdate({
        applicationHistory: patchApplicationById(candidate.applicationHistory, selectedApplicationId, {
          hrDecisionStatus: s,
          hrRemarks: remarks,
        }),
        status: s,
      });
    } else {
      onUpdate({ status: s, remarks });
    }
    setFlash(`Updated to ${s}`);
    setTimeout(onBack, 800);
  };
  useEffect(() => {
    if (detailTab !== "timeline" && detailTab !== "interview") {
      setInterviewAns(null);
      return;
    }
    if (selectedApplicationId == null) return;
    setInterviewLoading(true);
    fetch(
      `/api/admin/applications/${selectedApplicationId}/interview-answers`,
      apiFetchInit(),
    )
      .then((r) => (r.ok ? r.json() : { answers: [] }))
      .then(setInterviewAns)
      .catch(() => setInterviewAns({ answers: [] }))
      .finally(() => setInterviewLoading(false));
  }, [detailTab, selectedApplicationId]);
  const interviewAnswers = interviewAns?.answers || [];
  const hasTranscriptData =
    interviewAnswers.length > 0 || (bubbleTranscript || []).length > 0;
  // An interview that ended early is kept in the DB but hidden from HR until a
  // reattempt is completed (which flips the status back to 'completed').
  const selectedInterviewIncomplete =
    selectedAppRow?.interviewCompletionStatus === "incomplete_technical";
  const showTranscriptContent = hasTranscriptData && !selectedInterviewIncomplete;
  // A saved AI analysis for an incomplete interview was derived from a partial
  // transcript, so it is not valid — hide it too (mirrors the hidden transcript
  // and keeps the HR decision gated end-to-end).
  const showAnalysisForSelected =
    Boolean(analysisForSelected) && !selectedInterviewIncomplete;
  const handleExportTranscript = () => {
    const sections = buildTranscriptSections(interviewAnswers, bubbleTranscript || []);
    exportTranscriptPdf({
      candidateName: candidate.name,
      jobTitle: selectedAppRow?.job?.title || "Role",
      lang: transcriptLang,
      sections,
    });
  };
  return (
    <div className="min-h-dvh safe-bottom bg-surface-alt">
      <div className="bg-white border-b border-line text-ink-900 px-4 sm:px-6 py-3 sm:py-4 safe-top safe-x flex items-center gap-3"><button type="button" onClick={onBack} className="text-ink-400 hover:text-ink-900 text-sm">← Back</button><span className="font-bold">Candidate Details</span><Badge/></div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 safe-x space-y-4">
        {flash && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">✓ {flash}</div>}
        <div className="grid grid-cols-3 gap-2 border-b border-line pb-3">
          {[
            { id: "timeline", label: "Timeline" },
            { id: "interview", label: "Interview" },
            { id: "notes", label: "Notes" },
          ].map((t) => (
            <button key={t.id} type="button" onClick={() => setDetailTab(t.id)} className={`px-2 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-colors text-center ${detailTab === t.id ? "bg-brand-600 text-white" : "bg-white border border-line text-ink-600 hover:border-brand-200"}`}>{t.label}</button>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-line shadow-sm p-4 sm:p-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-brand-600 rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl font-black shrink-0">{candidate.name[0]}</div>
            <div className="min-w-0"><h1 className="text-xl sm:text-2xl font-black text-ink-900 break-words">{candidate.name}</h1><p className="text-ink-400 text-sm truncate">{candidate.email}</p>{candidate.phone && <p className="text-ink-400 text-sm">{candidate.phone}</p>}<p className="text-xs text-ink-400 mt-1 break-all">{candidate.id}{candidate.fromTalentPool && " · 🌟 From Talent Pool"}</p>
              {/* Interview conduct rules for the selected role. "Not granted" is
                  the normal state before a candidate reaches their interview, so
                  it stays muted rather than reading as a problem. */}
              {selectedAppRow ? (
                selectedAppRow.interviewRulesAck ? (
                  <p className="text-xs text-teal-700 mt-1">✓ Granted</p>
                ) : (
                  <p className="text-xs text-ink-400 mt-1">Not granted</p>
                )
              ) : null}</div>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1 shrink-0">
            <span className={`text-xs font-black uppercase px-3 py-1.5 rounded-lg ${SB[headerStatusKey] || SB[candidate.status] || "bg-slate-100"}`}>{headerStatusLabel !== "—" ? headerStatusLabel : candidate.status}</span>
            {headerReattemptNote ? <span className="text-[11px] font-semibold text-ink-500 normal-case">({headerReattemptNote})</span> : null}
          </div>
        </div>
        {detailTab === "timeline" ? (<>
        <div className="bg-white rounded-2xl border border-line shadow-sm p-5">
          <h2 className="text-xs font-black text-ink-400 uppercase mb-3">DPDPA Consent</h2>
          {candidate.consent ? <p className="text-sm text-ink-600">✓ Granted on {fmtDate(candidate.consentAt)}</p> : <p className="text-sm text-red-700">⚠ No consent — cannot process per §6</p>}
        </div>
        {appliedList.length > 0 && (
          <div className="bg-white rounded-2xl border border-line shadow-sm p-5">
            <h2 className="text-xs font-black text-ink-400 uppercase mb-1">Application History ({appliedList.length})</h2>
            <p className="text-xs text-ink-400 mb-3">Tap a role to view its full voice interview transcript below.</p>
            <div className="space-y-2">{appliedList.map((a) => (
              <button
                key={a.applicationId ?? `${a.jobId}-${a.appliedAt}`}
                type="button"
                onClick={() => a.applicationId != null && setSelectedApplicationId(a.applicationId)}
                className={`w-full text-left flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl transition-all ${a.applicationId === selectedApplicationId ? "ring-2 ring-brand-400 ring-offset-2" : ""} ${a.jobId === candidate.jobId ? "bg-brand-50 border border-brand-200" : "bg-surface-alt border border-transparent hover:border-line"}`}
              >
                <div>
                  <p className="font-bold text-ink-900 text-sm">{a.job.title}</p>
                  <p className="text-xs text-ink-400">{a.job.location} · Applied {fmtDate(a.appliedAt)}</p>
                  {a.interviewScheduledAt ? (
                    appSlotMissed(a) ? (
                      <p className="text-xs text-rose-600 font-semibold mt-0.5">Missed: {fmtDateTime(a.interviewScheduledAt)}</p>
                    ) : (
                      <p className="text-xs text-teal-700 font-semibold mt-0.5">Scheduled: {fmtDateTime(a.interviewScheduledAt)}</p>
                    )
                  ) : null}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {a.jobId === candidate.jobId && <span className="text-xs font-bold text-brand-600 bg-brand-100 px-2 py-0.5 rounded-full">Active</span>}
                  {(() => {
                    const roleStatus = applicationRoleStatus(a);
                    return (
                      <span className={`text-xs font-black uppercase px-2 py-0.5 rounded-lg ${SB[roleStatus] || "bg-slate-100 text-slate-600"}`}>
                        {applicationRoleStatusLabel(roleStatus)}
                      </span>
                    );
                  })()}
                </div>
              </button>
            ))}</div>
            {typeof onReschedule === "function" && candidate.consent && selectedAppRow && selectedApplicationId != null && selectedAppRow.interviewScheduledAt && interviewStartSlotStatus(selectedAppRow.interviewScheduledAt, false).tooLate && !applicationVoiceInterviewCompleted(selectedAppRow) && portalStatusLabel(candidate, selectedAppRow?.jobId || candidate.jobId) !== "REJECTED" ? (
              <div className="mt-3 pt-3 border-t border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-ink-400">Candidate missed or needs a new slot? Reschedule the interview.</p>
                <button type="button" onClick={() => setShowReschedule(true)} className="w-full sm:w-auto px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-sm font-bold rounded-xl shrink-0">📅 Reschedule interview</button>
              </div>
            ) : null}
          </div>
        )}
        {showCvSection && (
          <div className="bg-white rounded-2xl border border-line shadow-sm p-5">
            <button type="button" onClick={() => setShowCV(!showCV)} className="w-full flex items-center justify-between"><h2 className="text-xs font-black text-ink-400 uppercase">CV / Resume {candidate.cvFile?.ext ? `(${candidate.cvFile.ext.toUpperCase()})` : ""}</h2><span className="text-brand-500 text-xs font-bold">{showCV ? "▲ Hide" : "▼ Show"}</span></button>
            {showCV && (
              <div className="mt-4 bg-surface-alt rounded-xl p-4 border border-line">
                {hasStoredCv && candidate.cvFile && (
                  <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${String(candidate.cv || "").trim() ? "mb-3 pb-3 border-b border-line" : ""}`}>
                    <div className="flex items-center gap-2 text-sm min-w-0"><span className="text-2xl shrink-0">{fileIcon(candidate.cvFile.ext)}</span><div className="min-w-0"><p className="font-bold text-ink-900 truncate">{candidate.cvFile.name}</p><p className="text-xs text-ink-400">{fmtSize(candidate.cvFile.size)}</p></div></div>
                    <Button type="button" size="sm" className="w-full sm:w-auto shrink-0" onClick={() => downloadCvFile(candidate.cvFile, { candidateId: candidate.id })}>⬇ Download</Button>
                  </div>
                )}
                {String(candidate.cv || "").trim() ? (
                  <pre className="text-sm text-ink-600 whitespace-pre-wrap font-sans">{candidate.cv}</pre>
                ) : hasStoredCv ? (
                  <p className="text-sm text-ink-500">No extracted text — use Download for the stored file.</p>
                ) : null}
              </div>
            )}
          </div>
        )}
        {selectedApplicationId != null ? (
          <div className="bg-white rounded-2xl border border-line shadow-sm p-5">
            {selectedInterviewIncomplete ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                Interview was incomplete — transcript hidden. The candidate can request a reattempt; the full transcript will appear once a reattempt is completed.
              </p>
            ) : (
              <>
                {showTranscriptContent ? (
                  <div className="flex justify-end mb-3">
                    <button type="button" onClick={handleExportTranscript} title="Opens print dialog — choose Save as PDF" className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-lg">⬇ Export PDF</button>
                  </div>
                ) : null}
                <InterviewTranscriptPanel
                  jobTitle={selectedAppRow?.job?.title}
                  lang={transcriptLang}
                  loading={interviewLoading}
                  answers={interviewAnswers}
                  chatLines={bubbleTranscript || []}
                  collapsed={!showTranscript}
                  onToggleCollapsed={() => setShowTranscript(!showTranscript)}
                />
              </>
            )}
          </div>
        ) : null}
        {showAnalysisForSelected && (
          <div className="bg-white rounded-2xl border border-line shadow-sm p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3"><h2 className="text-xs font-black text-ink-400 uppercase">AI Analysis · {selectedAppRow?.job?.title || "—"}</h2><span className="px-3 py-1 rounded-lg text-white font-black text-xs bg-brand-600 self-start">{analysisForSelected.rec}</span></div>
            <p className="text-ink-600 text-sm mb-3">{analysisForSelected.summary}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><div className="bg-brand-50 rounded-xl p-3"><p className="text-xs text-brand-500 font-bold mb-1">Technical</p><p className="text-lg sm:text-2xl font-black text-brand-700">{analysisForSelected.tech}/10</p></div><div className="bg-teal-50 rounded-xl p-3"><p className="text-xs text-teal-500 font-bold mb-1">Communication</p><p className="text-lg sm:text-2xl font-black text-teal-700">{analysisForSelected.comm}/10</p></div></div>
          </div>
        )}
        </>) : null}
        {detailTab === "interview" ? (
          <div className="bg-white rounded-2xl border border-line shadow-sm p-5 space-y-4">
            <p className="text-sm text-ink-600">
              Full stepwise transcript (opening, HR script, AIRA follow-up, closing) is on the{" "}
              <button
                type="button"
                onClick={() => setDetailTab("timeline")}
                className="text-brand-600 font-bold underline"
              >
                Timeline
              </button>{" "}
              tab for the selected application.
            </p>
            {selectedApplicationId != null && showTranscriptContent ? (
              <div className="flex justify-end">
                <button type="button" onClick={handleExportTranscript} title="Opens print dialog — choose Save as PDF" className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-lg">⬇ Export PDF</button>
              </div>
            ) : null}
            {selectedApplicationId != null && selectedInterviewIncomplete ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                Interview was incomplete — transcript hidden. The candidate can request a reattempt; the full transcript will appear once a reattempt is completed.
              </p>
            ) : null}
            {selectedApplicationId != null && !selectedInterviewIncomplete && (
              <InterviewTranscriptPanel
                jobTitle={selectedAppRow?.job?.title}
                lang={transcriptLang}
                loading={interviewLoading}
                answers={interviewAnswers}
                chatLines={bubbleTranscript || []}
                showHeader={false}
              />
            )}
          </div>
        ) : null}
        {detailTab === "notes" ? (
        <>
        <div className="bg-white rounded-2xl border border-line shadow-sm p-5">
          <h2 className="text-xs font-black text-ink-400 uppercase mb-3">HR Actions · {selectedAppRow?.job?.title || "role"}</h2>
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks for this application…" className="mb-4"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {candidate.consent && portalStatusLabel(candidate, selectedAppRow?.jobId || candidate.jobId) === "APPLIED" && <Button type="button" fullWidth onClick={() => setStatus("SHORTLISTED")}>✓ Shortlist</Button>}
            {(portalStatusLabel(candidate, selectedAppRow?.jobId || candidate.jobId) === "INTERVIEWED" || showAnalysisForSelected) && (
              <Button type="button" variant="secondary" fullWidth onClick={() => typeof onAnalysis === "function" && onAnalysis(selectedApplicationId)}>📊 Analysis</Button>
            )}
            {typeof onReschedule === "function" && candidate.consent && selectedAppRow && selectedApplicationId != null && selectedAppRow.interviewScheduledAt && interviewStartSlotStatus(selectedAppRow.interviewScheduledAt, false).tooLate && !applicationVoiceInterviewCompleted(selectedAppRow) && portalStatusLabel(candidate, selectedAppRow?.jobId || candidate.jobId) !== "REJECTED" && (
              <button type="button" onClick={() => setShowReschedule(true)} className="w-full py-2.5 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-control text-sm transition-colors">📅 Reschedule</button>
            )}
            {portalStatusLabel(candidate, selectedAppRow?.jobId || candidate.jobId) !== "REJECTED" && candidate.status !== "WITHDRAWN" && <Button type="button" variant="danger" fullWidth onClick={() => { if (window.confirm("Reject this application?")) setStatus("REJECTED"); }}>✕ Reject</Button>}
          </div>
        </div>
        </>
        ) : null}
      </div>
      {showReschedule && (
        <InterviewScheduleModal
          jobTitle={selectedAppRow?.job?.title}
          busy={rescheduleBusy}
          onCancel={() => !rescheduleBusy && setShowReschedule(false)}
          onConfirm={async (iso) => {
            if (selectedApplicationId == null) return;
            setRescheduleBusy(true);
            try {
              await onReschedule(selectedApplicationId, iso);
              setShowReschedule(false);
              setFlash(`Interview rescheduled to ${fmtDateTime(iso)}`);
              setTimeout(() => setFlash(""), 5000);
            } catch (e) {
              toast.show({ tone: "danger", title: "Reschedule failed", message: "Could not reschedule. Please try again." });
            } finally {
              setRescheduleBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
